import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CardModalFormNames, CardModalFormValues } from '../types/CardModalFormNames';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { CardDetails } from 'containers/cards/cards';
import { clamp } from 'utilities/core-utilities';
import { getCardDetailRequest } from 'actions/cardActions';
import { useEffect } from 'react';

type CardMultiEditFormState = {
  originalCardValues: CardModalFormValues[];
  cardValues: CardModalFormValues[];
  currentIndex: number;
  selectedCards?: CardDetails[];
  status?: string;
};

const initialState: CardMultiEditFormState = {
  originalCardValues: [],
  cardValues: [],
  currentIndex: 0,
  selectedCards: [],
  status: '',
};

const selectCardFormStore = (state: RootState) => state.cardFormStore;

const cardMultiEditFormSlice = createSlice({
  name: 'cardMultiEditForm',
  initialState,
  reducers: {
    setMultiEditSelectedCards(state, action: PayloadAction<CardDetails[]>) {
      const selectedCards = action.payload;
      state.selectedCards = selectedCards;
      state.cardValues = selectedCards.map(() => ({}));
    },

    setMultiEditStatus(state, action: PayloadAction<string>) {
      state.status = action.payload;
    },

    setCurrentMultiEditFormValues(state, action: PayloadAction<CardModalFormValues>) {
      const values = action.payload;
      const index = state.currentIndex;

      // if the account number is set AND there is no value in the original card values
      if (values?.[CardModalFormNames.Account] && !state.originalCardValues?.[index]) {
        state.originalCardValues[index] = values;
      }

      state.cardValues[index] = values;
    },

    incrementMultiEditIndex(state) {
      var nextState = state.currentIndex + 1;
      state.currentIndex = clamp(nextState, 0, state.selectedCards.length - 1);
    },

    decrementMultiEditIndex(state) {
      var nextState = state.currentIndex - 1;
      state.currentIndex = clamp(nextState, 0, state.selectedCards.length - 1);
    },

    setCurrentMultiEditIndex(state, action: PayloadAction<number>) {
      state.currentIndex = action.payload;
    },

    resetMultiEdit(state) {
      state.originalCardValues = [];
      state.cardValues = [];
      state.currentIndex = 0;
      state.selectedCards = [];
      state.status = '';
    },
  },
});

export function useSelectMultiEditCardFormValues() {
  return useSelector((state: RootState) => selectCardFormStore(state).cardValues);
}

export function useSelectMutliEditCardIndex() {
  return useSelector((state: RootState) => selectCardFormStore(state).currentIndex);
}

export function useSelectMultiEditSelectedCards() {
  return useSelector((state: RootState) => selectCardFormStore(state).selectedCards);
}

export function useSelectCurrentCardDetail() {
  return useSelector((state: RootState) => {
    const cardFormStore = selectCardFormStore(state);
    return cardFormStore.selectedCards[cardFormStore.currentIndex];
  });
}

export function useSelectMultiEditCardsLength() {
  const cardDetails = useSelector((state: RootState) => selectCardFormStore(state).selectedCards);
  return cardDetails.length;
}

export function useSelectMultiEditCardCurrentValues() {
  return useSelector((state: RootState) => {
    const cardFormStore = selectCardFormStore(state);
    return cardFormStore.cardValues[cardFormStore.currentIndex];
  });
}

export function useSelectMultiEditCardStatus() {
  return useSelector((state: RootState) => selectCardFormStore(state).status);
}

export function useSelectMultiEditCardOriginalValues() {
  return useSelector((state: RootState) => selectCardFormStore(state).originalCardValues);
}

export function useMutliEditDefaultValuesEffect() {
  const dispatch = useDispatch();
  const currentCardDetail = useSelectCurrentCardDetail();
  const userStore = useSelector((state: RootState) => state.userStore);
  const assignedGrandfatherClientNumber = userStore.assignedGrandfatherClientNumber;
  const index = useSelectMutliEditCardIndex();
  const values = useSelector((state: RootState) => state.cardFormStore.cardValues);
  const cards = useSelectMultiEditSelectedCards();

  useEffect(() => {
    // Smart fetch
    // return if there is no card at the current index
    if (!cards[index]) return;
    // return if the card defaults are already fetched
    if (values[index] && Object.keys(values[index]).length > 0) return;

    dispatch(getCardDetailRequest(currentCardDetail.cardNumber, assignedGrandfatherClientNumber));
    // `cards` and `values` are Redux arrays — new object references on every dispatch.
    // Including them in the dep array would re-fire this effect on every store update,
    // producing repeated getCardDetailRequest calls (DW-66 pattern, 429 Too Many Requests).
    // `index` is the correct primitive signal: re-fetch only when the user navigates to
    // a different card. The guards inside the effect handle the "already loaded" case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);
}

export const {
  setMultiEditSelectedCards,
  setCurrentMultiEditFormValues,
  setCurrentMultiEditIndex,
  incrementMultiEditIndex,
  decrementMultiEditIndex,
  resetMultiEdit,
  setMultiEditStatus,
} = cardMultiEditFormSlice.actions;
export const cardFormReducer = cardMultiEditFormSlice.reducer;
